<template>
  <v-off-canvas
    id="template-duplicate-off-canvas"
    :backdrop-static="false"
    offcanvas-class="offcanvas-end"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header>
        <h5 class="offcanvas-title">{{ $t("tourClient.duplicateCase") }}</h5>
      </off-canvas-header>

      <off-canvas-body>
        <form
          v-if="!!agency && !!agency.slug"
          id="case-create-form"
          class="d-flex flex-column justify-content-between h-100"
        >
          <div>
            <fieldset>
              <legend class="visually-hidden">{{ $t("clientTour.basicInformation") }}</legend>
              <v-select-client-search
                id="case-create-client-search"
                v-model="localClientId"
                :agency="agency.slug"
                :client-id="localClientId"
              />
            </fieldset>
          </div>

          <v-button
            :disabled="!meta.valid || disableButton"
            :is-loading="accepted"
            class="btn-icon w-fit align-self-end mt-24"
            size="sm"
            variant="primary"
            @click="duplicateCase"
          >
            {{ $t("general.button.create") }}
            <v-icon icon="arrow-right" size="sm" space="ms-12" />
          </v-button>
        </form>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script lang="ts" setup name="TheCaseDuplicateOffCanvasV2">
import * as yup from "yup";
import { useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useAuthStore } from "@/stores/auth";
import VSelectClientSearch from "@/components/vendor/selectSearch/VSelectClientSearch.vue";
import api from "@/api";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useToast, POSITION } from "vue-toastification";

const emit = defineEmits(["closed"]);

const props = defineProps({
  caseId: {
    type: String,
    required: true,
  },
  tourId: {
    type: String,
    required: false,
  },
  clientId: {
    type: Number,
    required: false,
    default: null,
  },
  clientName: {
    type: String,
    required: false,
    default: "",
  },
  version: {
    type: String,
    required: false,
    default: "",
  },
});

const { t } = useI18n();
const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const { agency } = storeToRefs(authStore);

const rules = yup.object({
  localClientId: yup.number().nullable().label(t("general.shared.client")),
});

const initialValues = {
  localClientId: props.clientId ?? null,
};

const form = useFormValidation(rules, initialValues);

const { value: localClientId } = useField<number | null>("localClientId");

const { accepted, meta } = form;

const disableButton = ref(false);

const duplicateCase = async () => {
  if (!!agency.value) {
    try {
      const response = await api.case.caseDuplicate(
        agency.value.slug,
        Number(props.caseId),
        Number(localClientId.value)
      );

      toast.success(t("tourClient.duplicatedCaseSuccess"), {
        position: POSITION.BOTTOM_RIGHT,
      });

      toast.success(t("tourClient.redirectingNewCase"), {
        position: POSITION.BOTTOM_RIGHT,
        timeout: 10000,
      });

      await router.push({
        name: "case-v2.setup.show",
        params: { caseId: response?.content?.id },
      });

      emit("closed");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Something went wrong");
    }
  }
};
</script>
